import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { normalizeImg } from '@/lib/images';

interface SignalHighlight {
  id: string;
  label: string;
  value: number | null;
  unit?: string | null;
  note?: string | null;
  trend?: 'up' | 'down' | 'flat' | null;
}

interface TopMoment {
  id: string;
  title: string;
  kind: string;
  year: number | null;
  summary: string;
  impact?: { classrooms?: number | null; students?: number | null };
  media?: { image_url?: string | null };
  order?: number | null;
  visible?: boolean;
}

interface FeedEntry {
  id: string;
  type: 'story' | 'milestone' | 'digital_good' | 'press';
  title: string;
  summary?: string | null;
  media_url?: string | null;
  cta_text?: string | null;
  cta_url?: string | null;
  tag?: string | null;
  published_at?: string | null;
}

interface PublicConfig {
  theme: 'warm' | 'cool' | 'neutral' | 'bold';
  layout: 'standard' | 'signal_left' | 'signal_right';
  hero: { headline?: string | null; subheadline?: string | null; banner_url?: string | null };
  identity: { org_kind?: string | null; location?: string | null; industries?: string[]; short_bio?: string | null };
  focus_tags: string[];
  top_moments: TopMoment[];
  signal_highlights: SignalHighlight[];
  quote: { text?: string | null; person?: string | null; title?: string | null };
  social: { website?: string | null; linkedin?: string | null; x?: string | null; instagram?: string | null };
  modules: {
    show_stats?: boolean;
    show_quote?: boolean;
    show_activity?: boolean;
    show_signal_highlights?: boolean;
    show_moments?: boolean;
    show_feed?: boolean;
  };
  feed_entries?: FeedEntry[];
}

interface SponsorPayload {
  handle: string;
  display_name: string | null;
  tagline: string | null;
  website_url: string | null;
  logo_url: string | null;
  avatar_url: string | null;
  bio: string | null;
  public_config: PublicConfig;
  stats?: {
    total_credits: number | null;
    classroom_slots: number | null;
    students_estimate: number | null;
  };
}

interface SponsorResponse {
  sponsor: SponsorPayload;
}

const THEME_STYLES: Record<PublicConfig['theme'], { background: string; accent: string; chip: string }> = {
  warm: {
    background: 'from-amber-50 via-rose-50 to-orange-50',
    accent: 'text-amber-700',
    chip: 'bg-amber-100 text-amber-900',
  },
  cool: {
    background: 'from-sky-50 via-indigo-50 to-cyan-50',
    accent: 'text-sky-700',
    chip: 'bg-sky-100 text-sky-900',
  },
  neutral: {
    background: 'from-stone-50 via-sand to-stone-50',
    accent: 'text-cocoa',
    chip: 'bg-sand text-cocoa',
  },
  bold: {
    background: 'from-emerald-50 via-mint to-amber-50',
    accent: 'text-emerald-700',
    chip: 'bg-emerald-100 text-emerald-900',
  },
};

function formatNumber(value: number | null | undefined) {
  if (!Number.isFinite(Number(value))) return '—';
  return new Intl.NumberFormat('en-US').format(Number(value));
}

function formatImpact(moment: TopMoment) {
  const classrooms = Number(moment.impact?.classrooms ?? NaN);
  const students = Number(moment.impact?.students ?? NaN);
  const parts = [] as string[];
  if (Number.isFinite(classrooms)) parts.push(`${classrooms} classrooms`);
  if (Number.isFinite(students)) parts.push(`${students} students`);
  return parts.join(' · ');
}

export default function SponsorPublicProfilePage() {
  const params = useParams();
  const handleParam = params?.handle || params?.slug || '';
  const [searchParams] = useSearchParams();
  const preview = searchParams.get('preview') === '1';
  const handle = handleParam.trim().toLowerCase();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sponsor, setSponsor] = useState<SponsorPayload | null>(null);
  const [shareMessage, setShareMessage] = useState<string | null>(null);
  const themeKey = sponsor?.public_config?.theme || 'warm';
  const theme = THEME_STYLES[themeKey as keyof typeof THEME_STYLES] || THEME_STYLES.warm;

  useEffect(() => {
    if (!handle) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const url = `/api/sponsors/public/${encodeURIComponent(handle)}${preview ? '?preview=1' : ''}`;
        const response = await fetch(url, { headers: { accept: 'application/json' } });
        const json = (await response.json().catch(() => ({}))) as SponsorResponse;
        if (!response.ok) {
          throw new Error((json as any)?.error || 'Sponsor not found');
        }
        if (!cancelled) {
          setSponsor(json.sponsor);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Unable to load sponsor profile');
          setSponsor(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [handle, preview]);

  const heroImage = sponsor?.public_config?.hero?.banner_url?.trim()
    ? normalizeImg(sponsor.public_config.hero.banner_url)
    : null;
  const avatar = sponsor?.avatar_url?.trim() || sponsor?.logo_url?.trim() || null;
  const avatarUrl = avatar ? normalizeImg(avatar) : null;
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const moments = useMemo(() => {
    return (sponsor?.public_config?.top_moments || [])
      .filter((moment) => moment.visible !== false)
      .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
  }, [sponsor?.public_config?.top_moments]);

  const feedEntries = useMemo(() => {
    const entries = sponsor?.public_config?.feed_entries || [];
    return entries
      .filter((entry) => entry.title)
      .sort((a, b) => {
        const dateA = a.published_at ? new Date(a.published_at).getTime() : 0;
        const dateB = b.published_at ? new Date(b.published_at).getTime() : 0;
        return dateB - dateA;
      });
  }, [sponsor?.public_config?.feed_entries]);

  const signalHighlights = sponsor?.public_config?.signal_highlights || [];
  const modules = sponsor?.public_config?.modules || {};

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareMessage('Link copied!');
      setError(null);
    } catch (err) {
      console.warn('copy failed', err);
      setShareMessage('Copy not supported on this device.');
    }
  };

  if (!handle) {
    return <p className="mx-auto max-w-4xl px-4 py-10 text-cocoa/70">Sponsor handle missing.</p>;
  }

  if (loading) {
    return <p className="mx-auto max-w-4xl px-4 py-10 text-cocoa/70">Loading sponsor profile…</p>;
  }

  if (error) {
    return <p className="mx-auto max-w-4xl px-4 py-10 text-brick">{error}</p>;
  }

  if (!sponsor) {
    return <p className="mx-auto max-w-4xl px-4 py-10 text-cocoa/70">Sponsor not found.</p>;
  }

  return (
    <div className="min-h-screen bg-white">
      <div className={`bg-gradient-to-br ${theme.background} pb-12`}>
        <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 pt-10 sm:px-6">
          <section className="overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-soft">
            {heroImage ? (
              <div className="h-48 w-full overflow-hidden">
                <img src={heroImage} alt="Hero banner" className="h-full w-full object-cover" loading="lazy" />
              </div>
            ) : null}
            <div className="grid gap-6 p-6 md:grid-cols-[minmax(0,2.2fr)_minmax(0,1fr)]">
              <div className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-3xl border border-sand bg-white">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt={`${sponsor.display_name || 'Sponsor'} avatar`} className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <span className={`text-2xl font-semibold ${theme.accent}`}>{(sponsor.display_name || 'SP')[0]}</span>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div>
                      <p className="text-sm uppercase tracking-wide text-cocoa/50">@{sponsor.handle}</p>
                      <h1 className="text-3xl font-semibold text-cocoa sm:text-4xl">{sponsor.display_name || 'Sponsor'}</h1>
                      <p className="mt-1 text-sm text-cocoa/70">
                        {sponsor.public_config?.identity?.org_kind || 'Organization'} · {sponsor.public_config?.identity?.location || 'Location coming soon'}
                      </p>
                    </div>
                    {sponsor.public_config?.hero?.headline ? (
                      <p className="text-lg font-semibold text-cocoa/90">{sponsor.public_config.hero.headline}</p>
                    ) : null}
                    {sponsor.public_config?.hero?.subheadline ? (
                      <p className="text-sm text-cocoa/70">{sponsor.public_config.hero.subheadline}</p>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-sm font-semibold text-cocoa">
                  <button
                    type="button"
                    className="rounded-full border border-cocoa/30 bg-white px-4 py-2 shadow-sm transition hover:border-cocoa"
                  >
                    Follow
                  </button>
                  <button
                    type="button"
                    onClick={copyLink}
                    className="rounded-full border border-cocoa/30 bg-white px-4 py-2 shadow-sm transition hover:border-cocoa"
                  >
                    Share profile
                  </button>
                  <button
                    type="button"
                    className="rounded-full border border-cocoa/30 bg-white px-4 py-2 shadow-sm transition hover:border-cocoa"
                  >
                    Nominate
                  </button>
                  {sponsor.public_config?.social?.website && (
                    <a
                      href={sponsor.public_config.social.website}
                      className="rounded-full border border-cocoa/30 bg-white px-4 py-2 shadow-sm transition hover:border-cocoa"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Website
                    </a>
                  )}
                  {sponsor.public_config?.social?.linkedin && (
                    <a
                      href={sponsor.public_config.social.linkedin}
                      className="rounded-full border border-cocoa/30 bg-white px-4 py-2 shadow-sm transition hover:border-cocoa"
                      target="_blank"
                      rel="noreferrer"
                    >
                      LinkedIn
                    </a>
                  )}
                  {sponsor.public_config?.social?.x && (
                    <a
                      href={sponsor.public_config.social.x}
                      className="rounded-full border border-cocoa/30 bg-white px-4 py-2 shadow-sm transition hover:border-cocoa"
                      target="_blank"
                      rel="noreferrer"
                    >
                      X
                    </a>
                  )}
                  {sponsor.public_config?.social?.instagram && (
                    <a
                      href={sponsor.public_config.social.instagram}
                      className="rounded-full border border-cocoa/30 bg-white px-4 py-2 shadow-sm transition hover:border-cocoa"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Instagram
                    </a>
                  )}
                  {shareMessage ? <p className="w-full text-xs text-cocoa/60">{shareMessage}</p> : null}
                </div>
              </div>
              <div className="space-y-3 rounded-2xl border border-sand/80 bg-sand/30 p-4 text-sm text-cocoa">
                <p className="text-xs uppercase tracking-wide text-cocoa/60">Story focus</p>
                <p className="text-base text-cocoa/80">
                  {sponsor.public_config?.identity?.short_bio || sponsor.bio || 'This sponsor will add a short bio soon.'}
                </p>
                {sponsor.public_config?.focus_tags?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {sponsor.public_config.focus_tags.map((tag) => (
                      <span key={tag} className={`rounded-full px-3 py-1 text-xs font-semibold ${theme.chip}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </section>

          {modules.show_signal_highlights !== false && signalHighlights.length > 0 ? (
            <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {signalHighlights.map((signal) => (
                <div
                  key={signal.id}
                  className="rounded-2xl border border-sand/70 bg-white/90 p-4 shadow-soft"
                  aria-label={signal.label}
                >
                  <p className="text-xs uppercase tracking-wide text-cocoa/60">{signal.label}</p>
                  <p className="mt-1 text-2xl font-semibold text-cocoa">
                    {formatNumber(signal.value)} {signal.unit || ''}
                  </p>
                  {signal.note ? <p className="text-xs text-cocoa/60">{signal.note}</p> : null}
                </div>
              ))}
            </section>
          ) : null}

          <section className="grid gap-6 rounded-3xl border border-white/70 bg-white/90 p-6 shadow-soft md:grid-cols-[1.1fr,0.9fr]">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-cocoa">About</h2>
              <p className="text-sm leading-relaxed text-cocoa/80">
                {sponsor.public_config?.identity?.short_bio || sponsor.bio || 'This sponsor will add their story soon.'}
              </p>
              {sponsor.public_config?.identity?.industries?.length ? (
                <p className="text-xs uppercase tracking-wide text-cocoa/50">
                  Industries: {sponsor.public_config.identity.industries.join(', ')}
                </p>
              ) : null}
            </div>
            {modules.show_quote !== false && sponsor.public_config?.quote?.text ? (
              <div className="rounded-2xl border border-sand/70 bg-sand/20 p-4 text-cocoa">
                <p className="text-sm italic text-cocoa/80">“{sponsor.public_config.quote.text}”</p>
                {(sponsor.public_config.quote.person || sponsor.public_config.quote.title) && (
                  <p className="mt-2 text-xs uppercase tracking-wide text-cocoa/60">
                    {sponsor.public_config.quote.person}
                    {sponsor.public_config.quote.title ? `, ${sponsor.public_config.quote.title}` : ''}
                  </p>
                )}
              </div>
            ) : null}
          </section>

          {modules.show_moments !== false && moments.length ? (
            <section className="space-y-4 rounded-3xl border border-white/70 bg-white/90 p-6 shadow-soft">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-cocoa">Top moments</h2>
                <p className="text-sm text-cocoa/60">Curated highlights from this sponsor.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {moments.map((moment) => {
                  const imageUrl = moment.media?.image_url ? normalizeImg(moment.media.image_url) : null;
                  return (
                    <article key={moment.id} className="flex flex-col gap-3 rounded-2xl border border-sand/70 bg-white/90 p-4">
                      <div className="flex items-center justify-between">
                        <span className="rounded-full bg-sand px-3 py-1 text-xs font-semibold uppercase text-cocoa/70">
                          {moment.kind || 'Moment'}
                        </span>
                        {moment.year ? <span className="text-sm text-cocoa/60">{moment.year}</span> : null}
                      </div>
                      <h3 className="text-lg font-semibold text-cocoa">{moment.title || 'Moment'}</h3>
                      {moment.summary ? <p className="text-sm text-cocoa/70">{moment.summary}</p> : null}
                      {formatImpact(moment) && (
                        <p className="text-xs font-semibold text-cocoa/70">{formatImpact(moment)}</p>
                      )}
                      {imageUrl ? (
                        <img src={imageUrl} alt={moment.title || 'Moment'} className="h-36 w-full rounded-xl object-cover" loading="lazy" />
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </section>
          ) : null}

          {modules.show_stats !== false ? (
            <section className="grid gap-4 rounded-3xl border border-white/70 bg-white/90 p-6 shadow-soft sm:grid-cols-3">
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wide text-cocoa/60">Total credits contributed</p>
                <p className="text-2xl font-semibold text-cocoa">{formatNumber(sponsor.stats?.total_credits)}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wide text-cocoa/60">Estimated classrooms sparked</p>
                <p className="text-2xl font-semibold text-cocoa">{formatNumber(sponsor.stats?.classroom_slots)}</p>
              </div>
              <div className="space-y-1">
                <p className="text-xs uppercase tracking-wide text-cocoa/60">Estimated learners reached</p>
                <p className="text-2xl font-semibold text-cocoa">{formatNumber(sponsor.stats?.students_estimate)}</p>
              </div>
            </section>
          ) : null}

          {modules.show_feed !== false && feedEntries.length > 0 ? (
            <section className="space-y-4 rounded-3xl border border-white/70 bg-white/90 p-6 shadow-soft">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-cocoa">Updates & milestones</h2>
                <p className="text-sm text-cocoa/60">Recent activity from this sponsor</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {feedEntries.map((entry) => {
                  const imageUrl = entry.media_url ? normalizeImg(entry.media_url) : null;
                  const typeLabels: Record<string, string> = {
                    story: 'Story',
                    milestone: 'Milestone',
                    digital_good: 'Digital Good',
                    press: 'Press',
                  };
                  return (
                    <article key={entry.id} className="flex flex-col gap-3 rounded-2xl border border-sand/70 bg-white/90 p-4">
                      <div className="flex items-center justify-between">
                        <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase ${theme.chip}`}>
                          {entry.tag || typeLabels[entry.type] || 'Update'}
                        </span>
                        {entry.published_at ? (
                          <span className="text-sm text-cocoa/60">
                            {new Date(entry.published_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                        ) : null}
                      </div>
                      <h3 className="text-lg font-semibold text-cocoa">{entry.title}</h3>
                      {entry.summary ? <p className="text-sm text-cocoa/70">{entry.summary}</p> : null}
                      {imageUrl ? (
                        <img src={imageUrl} alt={entry.title} className="h-36 w-full rounded-xl object-cover" loading="lazy" />
                      ) : null}
                      {entry.cta_text && entry.cta_url ? (
                        <a
                          href={entry.cta_url}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-auto inline-flex w-fit items-center gap-1 rounded-full border border-cocoa/30 px-3 py-1.5 text-sm font-semibold text-cocoa transition hover:border-cocoa"
                        >
                          {entry.cta_text}
                          <span aria-hidden>→</span>
                        </a>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
